import { hyp } from '../core/exactMath';
import type { GridPoint } from '../core/types';
import { TerrainType, passabilityOf, terrainInfo, type Passability, type TerrainInfo } from './terrain';

/** Purely cosmetic object standing on a tile. Gameplay meaning comes from the terrain. */
export const DecorationType = {
  None: 0,
  Pine: 1,
  Oak: 2,
  Boulder: 3,
  Bush: 4,
  Flowers: 5,
  Reeds: 6,
  DeadTree: 7,
  Rubble: 8,
  /** Dark growth marking an infected spawn region (cosmetic). */
  Infestation: 9,
} as const;
export type DecorationType = (typeof DecorationType)[keyof typeof DecorationType];

/** Everything gameplay and UI need to know about one tile, in one read. */
export interface TileMetadata {
  readonly x: number;
  readonly y: number;
  readonly terrain: TerrainInfo;
  readonly walkable: boolean;
  readonly buildable: boolean;
  readonly passability: Passability;
  /** Tile is on the outermost ring of the map. */
  readonly edge: boolean;
  readonly decoration: DecorationType;
  /** Index of the player slot whose start (Command Center site centre) is this tile, or null. */
  readonly slot: number | null;
  /** Id of the infected spawn region covering this tile, if any. */
  readonly spawnZone: string | null;
}

/**
 * Where infected will emerge (Phase 5). Phase 2 only stores the region and
 * forbids building inside it.
 */
export interface SpawnRegion {
  readonly id: string;
  readonly center: GridPoint;
  /** Euclidean radius in tiles. */
  readonly radius: number;
}

export interface MapLayout {
  /**
   * Player start positions (centre of each Command Center site), in slot
   * order. A match with N players uses slots 0..N-1.
   */
  readonly playerSlots: readonly GridPoint[];
  readonly spawnRegions: readonly SpawnRegion[];
}

/**
 * Engine-independent tile data. Stored in flat typed arrays (index = y * width + x)
 * so it is cheap to copy, serialise (Phase 8) and query from pathfinding (Phase 6).
 */
export class WorldMap {
  readonly width: number;
  readonly height: number;
  readonly terrain: Uint8Array;
  /** Visual variant index per tile (0..3), chosen deterministically. */
  readonly variant: Uint8Array;
  readonly decoration: Uint8Array;
  readonly playerSlots: readonly GridPoint[];
  readonly spawnRegions: readonly SpawnRegion[];

  constructor(width: number, height: number, layout?: MapLayout) {
    if (!Number.isInteger(width) || !Number.isInteger(height) || width < 8 || height < 8) {
      throw new Error(`Invalid map size ${width}x${height}`);
    }
    this.width = width;
    this.height = height;
    this.terrain = new Uint8Array(width * height).fill(TerrainType.Grass);
    this.variant = new Uint8Array(width * height);
    this.decoration = new Uint8Array(width * height);
    this.playerSlots = layout?.playerSlots ?? [{ x: Math.floor(width / 2), y: Math.floor(height / 2) }];
    this.spawnRegions = layout?.spawnRegions ?? [];
  }

  /** Slot index whose start tile is (x, y), or null. */
  slotAt(x: number, y: number): number | null {
    const i = this.playerSlots.findIndex((s) => s.x === x && s.y === y);
    return i >= 0 ? i : null;
  }

  /** Spawn region containing the tile, or null. */
  spawnRegionAt(x: number, y: number): SpawnRegion | null {
    for (const r of this.spawnRegions) {
      if (hyp(x - r.center.x, y - r.center.y) <= r.radius) return r;
    }
    return null;
  }

  inBounds(x: number, y: number): boolean {
    return Number.isInteger(x) && Number.isInteger(y) && x >= 0 && y >= 0 && x < this.width && y < this.height;
  }

  index(x: number, y: number): number {
    if (!this.inBounds(x, y)) throw new RangeError(`Tile (${x}, ${y}) is outside the ${this.width}x${this.height} map`);
    return y * this.width + x;
  }

  getTerrain(x: number, y: number): TerrainType {
    return this.terrain[this.index(x, y)] as TerrainType;
  }

  setTerrain(x: number, y: number, t: TerrainType): void {
    this.terrain[this.index(x, y)] = t;
  }

  getDecoration(x: number, y: number): DecorationType {
    return this.decoration[this.index(x, y)] as DecorationType;
  }

  info(x: number, y: number): TerrainInfo {
    return terrainInfo(this.getTerrain(x, y));
  }

  isWalkable(x: number, y: number): boolean {
    return this.inBounds(x, y) && this.info(x, y).walkable;
  }

  isBuildable(x: number, y: number): boolean {
    return this.inBounds(x, y) && this.info(x, y).buildable;
  }

  isEdge(x: number, y: number): boolean {
    return this.inBounds(x, y) && (x === 0 || y === 0 || x === this.width - 1 || y === this.height - 1);
  }

  /** Full metadata for a tile, or null when (x, y) is off the map. */
  tileMetadata(x: number, y: number): TileMetadata | null {
    if (!this.inBounds(x, y)) return null;
    const terrain = this.info(x, y);
    return {
      x,
      y,
      terrain,
      walkable: terrain.walkable,
      buildable: terrain.buildable,
      passability: passabilityOf(terrain),
      edge: this.isEdge(x, y),
      decoration: this.getDecoration(x, y),
      slot: this.slotAt(x, y),
      spawnZone: this.spawnRegionAt(x, y)?.id ?? null,
    };
  }

  /**
   * Walkability as a flat grid (1 = walkable), indexed y * width + x.
   * The input format pathfinding (Phase 6) will consume.
   */
  walkableGrid(): Uint8Array {
    const out = new Uint8Array(this.width * this.height);
    for (let i = 0; i < out.length; i++) out[i] = terrainInfo(this.terrain[i] as TerrainType).walkable ? 1 : 0;
    return out;
  }

  /** Count tiles by terrain; used by tests and the debug overlay. */
  terrainCounts(): Map<TerrainType, number> {
    const counts = new Map<TerrainType, number>();
    for (const t of this.terrain) counts.set(t as TerrainType, (counts.get(t as TerrainType) ?? 0) + 1);
    return counts;
  }
}
